import { useEffect, useRef, useState } from 'react';
import quoteService from '../../../services/quote.service';

/** FSC types where the user enters the rate by hand (everything else is
 *  read-only: CURRENT / CUSTOMER auto-fill, FLAT / blank clear). */
export const FSC_EDITABLE_TYPES = new Set(['PCT%', 'MILES', 'DISCOUNTED']);

/**
 * The unit shown after the FSC rate field, by type. The rate is a percent of
 * the dray for CURRENT / PCT% / DISCOUNTED / CUSTOMER-STANDARD, and a dollar
 * rate per mile for MILES / CUSTOMER-PER-MILE, a flat $ per move for
 * CUSTOMER-FLAT. FLAT / blank fsctypes carry no unit.
 * `ratetype` resolves the CUSTOMER case ('STANDARD' | 'PER MILE' | 'PER GALLON' | 'FLAT').
 */
export function fscRateUnit(fsctype: string, ratetype: string | null): string {
  const t = (fsctype || '').trim().toUpperCase();
  if (t === 'MILES') return 'Per Mile';
  if (t === 'CUSTOMER') {
    if (ratetype === 'PER MILE') return 'Per Mile';
    if (ratetype === 'PER GALLON') return 'Per Gallon';
    if (ratetype === 'FLAT') return 'Flat';
    return '%';
  }
  if (t === 'PCT%' || t === 'CURRENT' || t === 'DISCOUNTED') return '%';
  return '';
}

interface Args {
  fsctype: string;
  /** Customer FSC tariff number (CUSTOMER type). */
  fsctariff: string;
  deliveryzone: string;
  /** The current FSC rate field value (drives the adjustment for editable types). */
  fscrate: string;
  /** Dray rate / total-with-charges the % applies to. */
  dray: string;
  miles: string;
  /** Master switch — false in read-only contexts (no fetch, no auto-fill). */
  enabled: boolean;
  /** Resolve the CUSTOMER tariff ratetype for DISPLAY even when disabled, so a
   *  saved PER-MILE quote viewed read-only still labels its FSC unit "Per Mile"
   *  (not "%"). Lookup-only: never unlocks the field or auto-fills the rate. */
  resolveReadOnly?: boolean;
  /** Fill the FSC rate field for the read-only types (CURRENT / CUSTOMER). */
  onFscRate: (rate: string) => void;
}

export interface QuoteFsc {
  /** The FSC rate field is read-only unless the type is user-entered. */
  fscReadOnly: boolean;
  /** CUSTOMER tariff ratetype ('STANDARD' | 'PER MILE' | 'PER GALLON' | 'FLAT') else null. */
  ratetype: string | null;
  /** Per-dray FSC $ for the estimated revenue: (dray + adjustment) × volume. */
  adjustment: number;
  /** The live rate resolved from /quotes/fsc for the auto-filled types
   *  (CURRENT / CUSTOMER), for DISPLAY — CURRENT is dynamic, so the fuel card
   *  shows this instead of a stored snapshot (which is 0 on a migrated quote
   *  until it's re-saved). Null for user-entered / FLAT / blank / unresolved. */
  resolvedRate: string | null;
}

/**
 * DIRECT-quote fuel-surcharge calculation (V1 quote.js updatefuelsurcharge +
 * estimatedrevenue). `fscReadOnly` is derived from the type immediately; the
 * rate (CURRENT / CUSTOMER) + the revenue adjustment are fetched (debounced)
 * from /quotes/fsc. The auto-FILL fires only on a real type/tariff/zone change
 * (never on mount, so an opened quote keeps its saved rate) and only for the
 * read-only types — the consumer clears the rate itself when switching types.
 */
export default function useQuoteFsc(a: Args): QuoteFsc {
  const { fsctype, fsctariff, deliveryzone, fscrate, dray, miles, enabled } = a;
  const resolveReadOnly = a.resolveReadOnly ?? false;
  const cbs = useRef({ onFscRate: a.onFscRate });
  cbs.current = { onFscRate: a.onFscRate };

  const ftype = (fsctype || '').trim().toUpperCase();
  const fscReadOnly = !enabled || !FSC_EDITABLE_TYPES.has(ftype);
  // Resolve whenever we're editing, OR for a read-only quote that opted into
  // display resolution and whose displayed value is derived, not stored:
  // CUSTOMER (for the ratetype label) and CURRENT (for the live OTR % — it's
  // dynamic, so we show the resolved rate rather than the stored snapshot).
  const wantResolve = enabled || (resolveReadOnly && (ftype === 'CUSTOMER' || ftype === 'CURRENT'));

  const [ratetype, setRatetype] = useState<string | null>(null);
  const [adjustment, setAdjustment] = useState(0);
  const [resolvedRate, setResolvedRate] = useState<string | null>(null);

  const initialDone = useRef(false);
  const typeKeyRef = useRef<string>('');
  useEffect(() => {
    // Blank / FLAT carry no surcharge — nothing to resolve, no adjustment.
    // (Mirrors the dray-rate hook, which skips its fetch when there's no input,
    // so non-FSC quotes never hit /quotes/fsc.)
    if (!wantResolve || !ftype || ftype === 'FLAT') {
      setRatetype(null);
      setAdjustment(0);
      setResolvedRate(null);
      typeKeyRef.current = [ftype, parseInt(fsctariff || '', 10) || 0, deliveryzone].join('|').toUpperCase();
      initialDone.current = true;
      return;
    }
    const m = parseFloat(miles || '') || 0;
    const d = parseFloat(dray || '') || 0;
    const r = parseFloat(fscrate || '') || 0;
    const tariff = parseInt(fsctariff || '', 10) || 0;
    const typeKey = [ftype, tariff, deliveryzone].join('|').toUpperCase();

    // Editing debounces (the user is typing); a read-only display resolve is a
    // one-shot lookup on a static quote, so fire it immediately (no flicker wait).
    const t = window.setTimeout(() => {
      const firstRun = !initialDone.current;
      const typeChanged = typeKey !== typeKeyRef.current;
      typeKeyRef.current = typeKey;
      initialDone.current = true;
      quoteService.getFsc({ fsctype: ftype, fsctariff: tariff || undefined, deliveryzone, fscrate: r, dray: d, miles: m })
        .then((res) => {
          setRatetype(res.ratetype);
          setAdjustment(res.adjustment || 0);
          setResolvedRate(res.fscRate != null ? String(res.fscRate) : null);
          // Auto-fill the read-only types (CURRENT / CUSTOMER) only on a genuine
          // type/tariff/zone change — not on mount, never the editable types (the
          // consumer cleared those on the type switch), and never in a read-only
          // display resolve (enabled=false: lookup only, the field is not owned).
          if (typeChanged && !firstRun && enabled && res.fscRate != null) {
            cbs.current.onFscRate(String(res.fscRate));
          }
        })
        .catch(() => { /* non-fatal — leave the rate / adjustment as-is */ });
    }, enabled ? 400 : 0);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantResolve, enabled, ftype, fsctariff, deliveryzone, fscrate, dray, miles]);

  return { fscReadOnly, ratetype, adjustment, resolvedRate };
}
